/**
 * Fridays, as the only dates a khutbah can be for.
 *
 * A Friday khutbah is delivered on a Friday. Letting a date control offer
 * Tuesday the 27th is offering a mistake — and one that is invisible later,
 * because a khutbah filed under the wrong day simply never appears where a
 * reader looks for it. Stepping by whole weeks makes the wrong answer
 * unreachable rather than merely discouraged, which is why the design
 * system's own control (`FridayNav.d.ts`) is a stepper and not a calendar.
 */

/** ISO-8601 `YYYY-MM-DD`, in local time — never `toISOString`, which is UTC. */
export function toIsoDate(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function parseIsoDate(iso: string): Date | null {
  // Local midnight, not UTC: `new Date('2026-10-30')` is parsed as UTC and
  // lands on the 29th anywhere west of Greenwich, which would silently shift
  // every khutbah by a day.
  const parsed = new Date(`${iso}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function isFriday(iso: string): boolean {
  return parseIsoDate(iso)?.getDay() === 5;
}

/**
 * The coming Friday — today, when today is a Friday.
 *
 * An editor uploading on Friday morning is uploading that morning's khutbah,
 * so skipping to next week would be wrong a seventh of the time.
 */
export function comingFriday(today: Date = new Date()): string {
  const date = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  date.setDate(date.getDate() + ((5 - date.getDay() + 7) % 7));
  return toIsoDate(date);
}

/** The same weekday, `weeks` away. Always another Friday. */
export function shiftWeeks(iso: string, weeks: number): string {
  const date = parseIsoDate(iso);
  if (date === null) {
    return iso;
  }
  date.setDate(date.getDate() + weeks * 7);
  return toIsoDate(date);
}

/** Snaps anything to a Friday, so a bad stored value cannot leak through. */
export function nearestFriday(iso: string): string {
  const date = parseIsoDate(iso);
  if (date === null) {
    return comingFriday();
  }
  date.setDate(date.getDate() + ((5 - date.getDay() + 7) % 7));
  return toIsoDate(date);
}

export function formatFriday(iso: string): string {
  const date = parseIsoDate(iso);
  if (date === null) {
    return iso;
  }
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * The Hijri date, from the Umm al-Qura calendar the Gulf ministries use.
 *
 * Display only and always prefixed ≈ by the caller, exactly as the contract
 * specifies: the tabulated calendar and the observed one do not always agree,
 * and this is a reading aid, not a ruling about which day it is.
 */
/**
 * Without the weekday word, for the stepper.
 *
 * The control is labelled "Friday this khutbah is for" and says only Fridays
 * can be chosen, so repeating "Friday," inside it buys nothing and costs the
 * width that made the date collide with the arrows.
 */
export function formatFridayShort(iso: string): string {
  const date = parseIsoDate(iso);
  if (date === null) {
    return iso;
  }
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function hijriApprox(iso: string): string | null {
  const date = parseIsoDate(iso);
  if (date === null) {
    return null;
  }
  try {
    // en-GB, not en: day-first, as the ministries' own notices write it.
    // Plain `en` renders "Jumada I 12, 1448 AH" — month first, and the month
    // as a numeral nobody writing an Arabic date would use.
    return new Intl.DateTimeFormat('en-GB-u-ca-islamic-umalqura', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  } catch {
    // An engine without the Umm al-Qura calendar gets no Hijri line, rather
    // than a Gregorian one relabelled — absent beats wrong.
    return null;
  }
}

/**
 * The cells of one month's calendar, Monday-first, padded to whole weeks.
 *
 * Returns every day the grid shows, including the leading and trailing days
 * that belong to the neighbouring months — the design system's Calendar draws
 * those muted rather than leaving holes.
 */
export function monthGrid(year: number, month: number): {
  iso: string;
  day: number;
  outside: boolean;
  friday: boolean;
}[] {
  const first = new Date(year, month, 1);
  // Monday-first, as the design system's day-of-week row is (Mo…Su), so
  // Sunday (getDay() 0) is the LAST column, not the first.
  const lead = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - lead);

  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    cells.push({
      iso: toIsoDate(date),
      day: date.getDate(),
      outside: date.getMonth() !== month,
      friday: date.getDay() === 5,
    });
    // Stop at a whole week once the month is behind us, so a short month
    // does not draw an entire empty sixth row.
    if (i >= 27 && i % 7 === 6) {
      const next = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i + 1);
      if (next.getMonth() !== month) {
        break;
      }
    }
  }
  return cells;
}

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
